/* eslint-disable @typescript-eslint/no-explicit-any */
import { ForwardedRef, forwardRef } from 'react';
import { LinkProps as BaseProps, Link as ReactDomLink } from 'react-router-dom';

import { useAuth } from 'providers/Auth';

import { AppRoute, RoutesType, routes, shouldBlockRoute } from './config';

import { AnyFunction, FirstParameter, IsUnknown } from 'types';

type RouterLinkProps = Omit<BaseProps, 'to'>;

type TargetRouteProps<To extends AppRoute> = RoutesType[To] extends { getPath: AnyFunction }
  ? FirstParameter<RoutesType[To]['getPath']>
  : unknown;

type TargetRouteQuery<To extends AppRoute> = RoutesType[To] extends { queryParams: string[] }
  ? { [Key in RoutesType[To]['queryParams'][number]]?: any }
  : unknown;

type FullRouteParams<To extends AppRoute> = TargetRouteProps<To> & TargetRouteQuery<To>;

// path + query params
/**
 * If:
 * - path is present, 'params' is required
 * - query is present, 'params' is optional
 */
type CustomRouteParams<To extends AppRoute> =
  IsUnknown<FullRouteParams<To>> extends true
    ? unknown
    : IsUnknown<TargetRouteProps<To>> extends true
      ? { params?: FullRouteParams<To> }
      : RoutesType[To] extends { isDynamic: true }
        ? { params: FullRouteParams<To> }
        : { params?: FullRouteParams<To> };

type RouteOptions = {
  [Route in AppRoute]: {
    to: Route;
  } & CustomRouteParams<Route>;
}[AppRoute];

export type LinkRouteMapping = { [Key in AppRoute]: Extract<RouteOptions, { to: Key }> };

/**
 * The necessary props to direct the user to a route
 *
 * Eg if a route has no path param = its only the **to** prop.
 *
 * If there's a path param, its to + the params
 */
export type RouteProps = LinkRouteMapping[keyof LinkRouteMapping];

export type LinkProps = RouterLinkProps & RouteOptions;

export const Link = forwardRef(
  ({ to, ...props }: LinkProps, ref: ForwardedRef<HTMLAnchorElement>) => {
    const routeConfig = routes[to] as any;

    const { user } = useAuth();

    if (!routeConfig) return <ReactDomLink to={to} ref={ref} {...props} />;

    if (shouldBlockRoute(routeConfig, user.data)) return null;

    const href = routeConfig.getPath?.((props as any)?.params) ?? routeConfig.path;

    return <ReactDomLink ref={ref} to={href} {...props} />;
  },
);

Link.displayName = 'Link';
